const HEIC_EXTENSION_PATTERN = /\.(heic|heif)$/i;
const DEFAULT_IMAGE_PREP_TIMEOUT_MS = 30000;

export const IMAGE_ACCEPT_WITH_HEIC = 'image/*,.heic,.heif,image/heic,image/heif';

export const IMAGE_DROPZONE_ACCEPT_WITH_HEIC = {
  'image/jpeg': ['.jpeg', '.jpg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/webp': ['.webp'],
  'image/bmp': ['.bmp'],
  'image/tiff': ['.tiff', '.tif'],
  'image/heic': ['.heic'],
  'image/heif': ['.heif'],
};

export const createUserVisibleImageError = (message) => {
  const error = new Error(message);
  error.isUserVisible = true;
  return error;
};

export const getImagePreparationErrorMessage = (
  error,
  fallback = 'That image could not be prepared. Please choose a JPEG, PNG, WEBP, HEIC, or HEIF image.'
) => (error?.isUserVisible && error.message ? error.message : fallback);

export const isHeicImageFile = (file) => {
  const fileType = file?.type?.toLowerCase() || '';
  const fileName = file?.name || '';

  return fileType === 'image/heic' || fileType === 'image/heif' || HEIC_EXTENSION_PATTERN.test(fileName);
};

const getJpegFileName = (fileName = 'image') => {
  const nameWithoutHeicExtension = fileName.replace(HEIC_EXTENSION_PATTERN, '');
  return `${nameWithoutHeicExtension || 'image'}.jpg`;
};

const withTimeout = (promise, timeoutMs, message) => new Promise((resolve, reject) => {
  const timeoutId = setTimeout(() => {
    reject(createUserVisibleImageError(message));
  }, timeoutMs);

  promise
    .then((value) => {
      clearTimeout(timeoutId);
      resolve(value);
    })
    .catch((error) => {
      clearTimeout(timeoutId);
      reject(error);
    });
});

export const convertHeicToJpeg = async (file, timeoutMs = DEFAULT_IMAGE_PREP_TIMEOUT_MS) => {
  const heic2anyModule = await withTimeout(
    import('heic2any'),
    timeoutMs,
    'HEIC conversion could not start. Please choose another image.'
  );
  const heic2any = heic2anyModule.default || heic2anyModule;
  const converted = await withTimeout(
    heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.92,
    }),
    timeoutMs,
    'HEIC conversion took too long. Please choose another image.'
  );
  const convertedBlob = Array.isArray(converted) ? converted[0] : converted;

  if (!convertedBlob) {
    throw createUserVisibleImageError('HEIC conversion failed. Please choose another image.');
  }

  return new File([convertedBlob], getJpegFileName(file.name), {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
};

const loadImageFromFile = (file, timeoutMs) => new Promise((resolve, reject) => {
  const imageUrl = URL.createObjectURL(file);
  const image = new Image();
  const timeoutId = setTimeout(() => {
    URL.revokeObjectURL(imageUrl);
    reject(createUserVisibleImageError('Image preparation took too long. Please choose another image.'));
  }, timeoutMs);

  image.onload = () => {
    clearTimeout(timeoutId);
    URL.revokeObjectURL(imageUrl);
    resolve(image);
  };

  image.onerror = () => {
    clearTimeout(timeoutId);
    URL.revokeObjectURL(imageUrl);
    reject(createUserVisibleImageError('Unable to read selected image. Please choose another image.'));
  };

  image.src = imageUrl;
});

const canvasToBlob = (canvas, type, quality, timeoutMs) => new Promise((resolve, reject) => {
  const timeoutId = setTimeout(() => {
    reject(createUserVisibleImageError('Image optimization took too long. Please choose another image.'));
  }, timeoutMs);

  canvas.toBlob((blob) => {
    clearTimeout(timeoutId);

    if (blob) {
      resolve(blob);
      return;
    }

    reject(createUserVisibleImageError('Unable to prepare image for upload. Please choose another image.'));
  }, type, quality);
});

export const optimizeImageFile = async (
  file,
  {
    maxDimension = 2400,
    quality = 0.9,
    timeoutMs = DEFAULT_IMAGE_PREP_TIMEOUT_MS,
  } = {}
) => {
  if (!file || typeof window === 'undefined' || typeof document === 'undefined') {
    return file;
  }

  const image = await loadImageFromFile(file, timeoutMs);
  const width = image.naturalWidth || image.width || 1;
  const height = image.naturalHeight || image.height || 1;
  const scale = Math.min(1, maxDimension / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));

  const context = canvas.getContext('2d');
  if (!context) {
    throw createUserVisibleImageError('Unable to prepare image for upload. Please choose another image.');
  }

  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  const blob = await canvasToBlob(canvas, 'image/jpeg', quality, timeoutMs);
  const fileName = (file.name || 'image').replace(/\.[^.]+$/, '') || 'image';

  return new File([blob], `${fileName}.jpg`, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
};

export const prepareImageFileForUpload = async (
  file,
  {
    optimizeHeic = true,
    maxDimension = 2400,
    quality = 0.9,
    timeoutMs = DEFAULT_IMAGE_PREP_TIMEOUT_MS,
  } = {}
) => {
  if (!file) {
    return file;
  }

  const sourceWasHeic = isHeicImageFile(file);
  let imageFile = sourceWasHeic ? await convertHeicToJpeg(file, timeoutMs) : file;

  if (sourceWasHeic && optimizeHeic) {
    imageFile = await optimizeImageFile(imageFile, { maxDimension, quality, timeoutMs });
  }

  return imageFile;
};

export const prepareImageFilesForUpload = async (files, options) => {
  const preparedFiles = [];

  for (const file of Array.from(files || [])) {
    preparedFiles.push(await prepareImageFileForUpload(file, options));
  }

  return preparedFiles;
};

export const readImageFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();

  reader.onload = () => {
    if (reader.readyState === 2) {
      resolve(reader.result);
    }
  };

  reader.onerror = () => reject(createUserVisibleImageError('Unable to read selected image. Please choose another image.'));
  reader.readAsDataURL(file);
});
